import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';

interface ButtonStyle {
  textColor: string;
  backgroundColor: string;
  borderColor: string;
  textTransform: 'none' | 'uppercase' | 'lowercase';
}

interface ButtonShadowSettings {
  type: 'none' | 'shadow' | 'glow';
  color: string;
  strength: number;
}

interface ButtonStyleEditorProps {
  restState: ButtonStyle;
  hoverState: ButtonStyle;
  restShadow: ButtonShadowSettings;
  hoverShadow: ButtonShadowSettings;
  onRestStateChange: (style: ButtonStyle) => void;
  onHoverStateChange: (style: ButtonStyle) => void;
  onRestShadowChange: (shadow: ButtonShadowSettings) => void;
  onHoverShadowChange: (shadow: ButtonShadowSettings) => void;
  primaryColor: string;
  secondaryColor: string;
  showColorControls?: boolean; // New prop to control color visibility
}

/**
 * Generate CSS shadow for preview
 */
const generatePreviewShadow = (shadowSettings: ButtonShadowSettings): string => {
  if (shadowSettings.type === 'none') {
    return 'none';
  }
  
  // Simple color parsing for hex colors
  const hex = shadowSettings.color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const opacity = shadowSettings.strength;
  
  if (shadowSettings.type === 'shadow') {
    const blur = Math.round(15 * shadowSettings.strength);
    const spread = Math.round(5 * shadowSettings.strength);
    const yOffset = Math.round(6 * shadowSettings.strength);
    return `0 ${yOffset}px ${blur}px ${spread}px rgba(${r}, ${g}, ${b}, ${opacity})`;
  } else if (shadowSettings.type === 'glow') {
    const blur = Math.round(20 * shadowSettings.strength);
    const spread = Math.round(3 * shadowSettings.strength);
    return `0 0 ${blur}px ${spread}px rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  
  return 'none';
};

/**
 * ButtonStyleEditor - Comprehensive button styling editor for rest and hover states
 */
const ButtonStyleEditor: React.FC<ButtonStyleEditorProps> = ({
  restState,
  hoverState,
  restShadow,
  hoverShadow,
  onRestStateChange,
  onHoverStateChange,
  onRestShadowChange,
  onHoverShadowChange,
  primaryColor,
  secondaryColor,
  showColorControls = true
}) => {
  const [activeTab, setActiveTab] = React.useState<'rest' | 'hover'>('rest');
  
  const colorPresets = [
    { name: 'Primary', color: primaryColor },
    { name: 'Secondary', color: secondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Transparent', color: 'transparent' },
    { name: 'Gold', color: '#c5b068' },
    { name: 'Blue', color: '#3f92ff' },
    { name: 'Green', color: '#3fc060' }
  ];

  const updateStyle = (
    state: 'rest' | 'hover',
    property: keyof ButtonStyle,
    value: string
  ) => {
    const currentStyle = state === 'rest' ? restState : hoverState;
    const newStyle = { ...currentStyle, [property]: value };
    
    if (state === 'rest') {
      onRestStateChange(newStyle);
    } else {
      onHoverStateChange(newStyle);
    }
  };

  const updateShadow = (
    state: 'rest' | 'hover',
    property: keyof ButtonShadowSettings,
    value: string | number
  ) => {
    const currentShadow = state === 'rest' ? restShadow : hoverShadow;
    const newShadow = { ...currentShadow, [property]: value };
    
    if (state === 'rest') {
      onRestShadowChange(newShadow);
    } else {
      onHoverShadowChange(newShadow);
    }
  };

  const copyRestToHover = () => {
    onHoverStateChange({ ...restState });
    onHoverShadowChange({ ...restShadow });
  };

  const copyHoverToRest = () => {
    onRestStateChange({ ...hoverState });
    onRestShadowChange({ ...hoverShadow });
  };

  const resetToDefaults = (state: 'rest' | 'hover') => {
    const defaultRestStyle: ButtonStyle = {
      textColor: '#ffffff',
      backgroundColor: primaryColor,
      borderColor: 'transparent',
      textTransform: 'none'
    };

    const defaultHoverStyle: ButtonStyle = {
      textColor: '#ffffff',
      backgroundColor: secondaryColor,
      borderColor: 'transparent',
      textTransform: 'none'
    };

    const defaultRestShadow: ButtonShadowSettings = {
      type: 'none',
      color: '#000000',
      strength: 0.3
    };

    const defaultHoverShadow: ButtonShadowSettings = {
      type: 'shadow',
      color: '#000000',
      strength: 0.4
    };

    if (state === 'rest') {
      onRestStateChange(defaultRestStyle);
      onRestShadowChange(defaultRestShadow);
    } else {
      onHoverStateChange(defaultHoverStyle);
      onHoverShadowChange(defaultHoverShadow);
    }
  };

  const renderColorPicker = (
    state: 'rest' | 'hover',
    property: keyof ButtonStyle,
    label: string,
    style: ButtonStyle
  ) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start gap-2">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: style[property] === 'transparent' ? '#f0f0f0' : style[property] }}
              />
              {style[property]}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-3">
              <Input
                type="color"
                value={style[property] === 'transparent' ? '#ffffff' : style[property]}
                onChange={(e) => updateStyle(state, property, e.target.value)}
                className="w-full h-10"
              />
              <Input
                type="text"
                value={style[property]}
                onChange={(e) => updateStyle(state, property, e.target.value)}
                placeholder="#ffffff"
                className="font-mono text-sm"
              />
              <div className="grid grid-cols-2 gap-1">
                {colorPresets.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    size="sm"
                    onClick={() => updateStyle(state, property, preset.color)}
                    className="justify-start gap-2 h-auto p-2"
                  >
                    <div 
                      className="w-3 h-3 rounded border border-gray-300"
                      style={{ backgroundColor: preset.color === 'transparent' ? '#f0f0f0' : preset.color }}
                    />
                    <span className="text-xs">{preset.name}</span>
                  </Button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  const renderStyleEditor = (
    state: 'rest' | 'hover',
    style: ButtonStyle
  ) => {
    const currentShadow = state === 'rest' ? restShadow : hoverShadow;
    
    return (
      <div className="space-y-4">
        {showColorControls && (
          <>
            {renderColorPicker(state, 'textColor', 'Text Colour', style)}
            {renderColorPicker(state, 'backgroundColor', 'Background Colour', style)}
            {renderColorPicker(state, 'borderColor', 'Border Colour', style)}
          </>
        )}

        {/* Text Capitalization */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Text Capitalisation</Label>
          <Select
            value={style.textTransform}
            onValueChange={(value: 'none' | 'uppercase' | 'lowercase') => 
              updateStyle(state, 'textTransform', value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Normal</SelectItem>
              <SelectItem value="uppercase">UPPERCASE</SelectItem>
              <SelectItem value="lowercase">lowercase</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Shadow/Glow Controls */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Shadow & Glow</Label>
          
          {/* Shadow Type Selector */}
          <div className="space-y-2">
            <Label className="text-xs">Effect Type</Label>
            <Select
              value={currentShadow.type}
              onValueChange={(value: 'none' | 'shadow' | 'glow') => updateShadow(state, 'type', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="shadow">Shadow</SelectItem>
                <SelectItem value="glow">Glow</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Show color and strength controls only if not 'none' */}
          {currentShadow.type !== 'none' && (
            <>
              {/* Shadow Color */}
              <div className="space-y-2">
                <Label className="text-xs">Colour</Label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start gap-2">
                        <div 
                          className="w-4 h-4 rounded border border-gray-300"
                          style={{ backgroundColor: currentShadow.color }}
                        />
                        {currentShadow.color}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64">
                      <div className="space-y-3">
                        <Input
                          type="color"
                          value={currentShadow.color}
                          onChange={(e) => updateShadow(state, 'color', e.target.value)}
                          className="w-full h-10"
                        />
                        <Input
                          type="text"
                          value={currentShadow.color}
                          onChange={(e) => updateShadow(state, 'color', e.target.value)}
                          placeholder="#000000"
                          className="font-mono text-sm"
                        />
                        <div className="grid grid-cols-2 gap-1">
                          {colorPresets.map((preset) => (
                            <Button
                              key={preset.name}
                              variant="outline"
                              size="sm"
                              onClick={() => updateShadow(state, 'color', preset.color)}
                              className="justify-start gap-2 h-auto p-2"
                            >
                              <div 
                                className="w-3 h-3 rounded border border-gray-300"
                                style={{ backgroundColor: preset.color === 'transparent' ? '#f0f0f0' : preset.color }}
                              />
                              <span className="text-xs">{preset.name}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Shadow Strength */}
              <div className="space-y-2">
                <Label className="text-xs">Strength: {currentShadow.strength.toFixed(1)}</Label>
                <Slider
                  value={[currentShadow.strength]}
                  onValueChange={([value]) => updateShadow(state, 'strength', value)}
                  min={0}
                  max={1}
                  step={0.1}
                  className="w-full"
                />
              </div>
            </>
          )}
        </div>

        {/* Reset Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => resetToDefaults(state)}
          className="w-full"
        >
          Reset to Defaults
        </Button>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Button Styling</CardTitle>
        <div className="flex gap-2">
          {activeTab === 'rest' && (
            <Button
              variant="outline"
              size="sm"
              onClick={copyRestToHover}
              className="text-xs"
            >
              Copy Rest → Hover
            </Button>
          )}
          {activeTab === 'hover' && (
            <Button
              variant="outline"
              size="sm"
              onClick={copyHoverToRest}
              className="text-xs"
            >
              Copy Hover → Rest
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="rest" className="w-full" onValueChange={(value) => setActiveTab(value as 'rest' | 'hover')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="rest">Rest State</TabsTrigger>
            <TabsTrigger value="hover">Hover State</TabsTrigger>
          </TabsList>
          
          <TabsContent value="rest" className="mt-4">
            {renderStyleEditor('rest', restState)}
          </TabsContent>
          
          <TabsContent value="hover" className="mt-4">
            {renderStyleEditor('hover', hoverState)}
          </TabsContent>
        </Tabs>

        {/* Preview */}
        <div className="mt-6 p-4 bg-gray-100 rounded-md">
          <Label className="text-sm font-medium mb-2 block">Preview</Label>
          <div className="space-y-3">
            <div className="flex gap-3">
              <button 
                className="px-4 py-2 rounded transition-all duration-300 border-2"
                style={{
                  color: showColorControls ? restState.textColor : '#ffffff',
                  backgroundColor: showColorControls ? restState.backgroundColor : primaryColor,
                  borderColor: showColorControls ? restState.borderColor : 'transparent',
                  textTransform: restState.textTransform,
                  boxShadow: generatePreviewShadow(restShadow)
                }}
              >
                Button (Rest)
              </button>
              <button 
                className="px-4 py-2 rounded transition-all duration-300 border-2"
                style={{
                  color: showColorControls ? hoverState.textColor : '#ffffff',
                  backgroundColor: showColorControls ? hoverState.backgroundColor : primaryColor,
                  borderColor: showColorControls ? hoverState.borderColor : 'transparent',
                  textTransform: hoverState.textTransform,
                  boxShadow: generatePreviewShadow(hoverShadow)
                }}
              >
                Button (Hover)
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ButtonStyleEditor;